import { useCallback, useMemo } from 'react';
import { Element } from '@thive/canvas';
import { SingularStationArea } from '@thive/fddm-types';
import { Vector3 } from 'three';
import { t } from 'i18next';

import { useFlowsCallbacks } from '@/modules/floorplanDesign/hooks/useFlowsCallbacks';
import { FlowStopType, useActiveFlowStore } from '@/modules/flowsV2/store/useActiveFlowStore';
import { FlowServiceFactory } from '@/modules/flowsV2/service/FlowServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';
import {
  getPersistedFlowEndpointId,
  getShuttleStorageAreaByBaseId,
  isShuttleFlowEndpoint,
} from '@/modules/flowsV2/flowEndpoint';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { resolveClickedGroupArea } from '@/modules/canvas/helpers/resolveClickedGroupArea';
import { isVisibleOnCanvas } from '@/modules/canvas/helpers/isVisibleOnCanvas';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { logger } from '@common/logging/logger';

const isSameFlowEndpoint = (sourceId: string, targetId: string): boolean =>
  getPersistedFlowEndpointId(sourceId) === getPersistedFlowEndpointId(targetId);

export const useFlowClick = () => {
  const { isValidSource, isValidTarget, getFlowStopAttachPoint, getFlowDataFromElement } =
    useFlowsCallbacks();

  const currentFlowSourceId = useActiveFlowStore((state) => state.currentFlowSourceId);
  const currentFlowSourceFlowStopType = useActiveFlowStore(
    (state) => state.currentFlowSourceFlowStopType,
  );
  const currentFlowSourceLoadHandlingType = useActiveFlowStore(
    (state) => state.currentFlowSourceLoadHandlingType,
  );

  const flowSvc = useMemo(() => FlowServiceFactory.getInstance().createFlowService(), []);
  const selectedToolId = useAppStore((state) => state.selectedToolId);
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const getFlowDataFromClick = useCallback(
    (element: Element, clickedPosition: Vector3) => {
      const state = useFloorplanDesignStore.getState();
      const directArea = state.getAreaById(element.id);
      const directFlowStop = state.getFlowStopById(element.id);

      if (directArea || directFlowStop) return getFlowDataFromElement(element.id);

      const shuttleStorageArea = getShuttleStorageAreaByBaseId(element.id);

      if (shuttleStorageArea) return getFlowDataFromElement(element.id);

      const eligibleAreaIds = new Set(useAppStore.getState().enabling.getClickableElementIds());
      const clickedGroupArea = resolveClickedGroupArea(element, clickedPosition, {
        eligibleAreaIds,
        isAreaPickable: isVisibleOnCanvas,
        prioritizeSmallestArea: true,
      });

      if (clickedGroupArea) return getFlowDataFromElement(clickedGroupArea.id);

      return getFlowDataFromElement(element.id);
    },
    [getFlowDataFromElement],
  );

  const firstClick = useCallback(
    (
      id: string,
      flowStopType: FlowStopType,
      targetLoadHandlingType: SingularStationArea['loadHandlingType'],
    ) => {
      if (
        selectedToolId !== 'transport_flow' &&
        selectedToolId !== 'consolidation_flow' &&
        selectedToolId !== 'external_flow'
      )
        return;

      if (!isValidSource(id, flowStopType, targetLoadHandlingType)) return;

      const pointA = getFlowStopAttachPoint(id, flowStopType);
      if (!pointA) {
        logger.error('could not get attach point for flow stop');
        return;
      }

      flowSvc.startFlow(pointA, id, flowStopType, targetLoadHandlingType, selectedToolId);
    },
    [isValidSource, getFlowStopAttachPoint, flowSvc, selectedToolId],
  );

  const secondClick = useCallback(
    (
      id: string,
      flowStopType: FlowStopType,
      targetLoadHandlingType: SingularStationArea['loadHandlingType'],
    ) => {
      if (!currentFlowSourceLoadHandlingType) {
        logger.error('currentFlowSourceLoadHandlingType', currentFlowSourceLoadHandlingType);
        return;
      }

      if (!currentFlowSourceId || !currentFlowSourceFlowStopType) {
        logger.error('invalid flow source');
        return;
      }

      if (
        selectedToolId !== 'transport_flow' &&
        selectedToolId !== 'consolidation_flow' &&
        selectedToolId !== 'external_flow'
      )
        return;

      if (isSameFlowEndpoint(currentFlowSourceId, id)) {
        showSnackbar(
          t('errors:flows.same_source_and_destination', {
            defaultValue: 'Source and destination cannot be the same.',
          }),
        );
        return;
      }

      const sourceIsShuttleEndpoint = isShuttleFlowEndpoint(
        currentFlowSourceFlowStopType,
        currentFlowSourceId,
      );
      const targetIsShuttleEndpoint = isShuttleFlowEndpoint(flowStopType, id);

      if (sourceIsShuttleEndpoint && targetIsShuttleEndpoint) {
        showSnackbar(t('errors:flow.shuttle_to_shuttle_not_supported'));
        return;
      }

      if (!isValidTarget(id, flowStopType, targetLoadHandlingType)) {
        logger.error('invalid target for flow');
        return;
      }

      const pointA = getFlowStopAttachPoint(id, flowStopType);
      if (!pointA) {
        logger.error('could not get attach point for flow stop');
        return;
      }

      flowSvc.endFlow(currentFlowSourceId, id);
    },
    [
      currentFlowSourceLoadHandlingType,
      currentFlowSourceId,
      currentFlowSourceFlowStopType,
      selectedToolId,
      isValidTarget,
      getFlowStopAttachPoint,
      flowSvc,
      showSnackbar,
    ],
  );

  const flowClick = useCallback(
    (element: Element, clickedPosition: Vector3, isSecondClick: boolean) => {
      const data = getFlowDataFromClick(element, clickedPosition);
      if (!data) {
        logger.error('Could not retrieve flow stop data from clicked element', element);
        return;
      }

      const { selectedFlowStopOrAreaId, flowStopType, loadHandlingType } = data;
      if (!loadHandlingType) {
        logger.error('Clicked element has no load handling type', element);
        return;
      }

      if (isSecondClick) {
        secondClick(selectedFlowStopOrAreaId, flowStopType, loadHandlingType);
        return;
      }

      firstClick(selectedFlowStopOrAreaId, flowStopType, loadHandlingType);
    },
    [firstClick, getFlowDataFromClick, secondClick],
  );

  return { flowClick };
};
